import { NavLink } from "react-router-dom";
import { Logo } from "@/shared/ui/logo";
import {
  LayoutDashboard,
  Blocks,
  Users,
  Shield,
  Briefcase,
} from "lucide-react";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import { useLanguage } from "@/shared/hooks/use-language";
import { useState, useEffect, useRef } from "react";
import type { ScopeContext } from "@/features/auth/model/types";
import { getCachedProductIds, getProductIds, errorCodeOf } from "@/features/auth/api/product-binding";
import { ScopeSwitcher } from "@/features/auth/ui/ScopeSwitcher";
import { DebugLogger } from "@/shared/lib/debug";

const menuItems = [
  {
    key: "dashboard",
    icon: LayoutDashboard,
    path: "/console",
  },
  {
    key: "access",
    icon: Shield,
    path: "/console/access",
  },
  {
    key: "integrations",
    icon: Blocks,
    path: "/console/integrations",
  },
  {
    key: "investors",
    icon: Users,
    path: "/console/investors",
    requiredProduct: "tracker",
  },
  {
    key: "portfolio",
    icon: Briefcase,
    path: "/console/portfolio",
    requiredProduct: "tracker",
  },
];

const STAGGER_MS = 50;

type ProductState =
  | { status: "loading" }
  | { status: "ready"; productIds: string[] }
  | { status: "unknown" };

function readInitialProductState(): ProductState {
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  if (!sessionId) return { status: "loading" };
  const context = ScopeContextStorage.getScopeContext(sessionId);
  if (!context) return { status: "loading" };
  const cached = getCachedProductIds(sessionId, context);
  return cached ? { status: "ready", productIds: cached } : { status: "loading" };
}

// A scope-less console is redirected out by ConsoleLayout, so these states are a frame on
// the way to the login funnel, not an answer about the account. "unknown" is reserved for a
// lookup that actually ran and failed, which is the only case a retry can change.

export const SidebarContent = ({
  onNavigate,
  onExitNavigate,
}: {
  onNavigate?: () => void;
  onExitNavigate?: (to: string, state?: unknown) => void;
}) => {
  const { t } = useLanguage();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [scopeContext, setScopeContext] = useState<ScopeContext | null>(null);
  const [productState, setProductState] = useState<ProductState>(readInitialProductState);
  const [retryToken, setRetryToken] = useState(0);
  const [retrying, setRetrying] = useState(false);
  // Read inside the effect without making it a dependency: listing it there would re-fire
  // the lookup on every flip of the flag the lookup itself sets.
  const retryingRef = useRef(false);

  useEffect(() => {
    const endRetry = () => {
      retryingRef.current = false;
      setRetrying(false);
    };

    const sessionId = ScopeContextStorage.getCurrentSessionId();
    setSessionId(sessionId);
    if (!sessionId) {
      endRetry();
      return;
    }
    const context = ScopeContextStorage.getScopeContext(sessionId);
    setScopeContext(context);
    if (!context) {
      endRetry();
      return;
    }

    let cancelled = false;
    // A retry keeps the failure row on screen so its own button is never unmounted from
    // under the focus that pressed it.
    setProductState((current) =>
      current.status === "ready" || retryingRef.current ? current : { status: "loading" },
    );
    getProductIds(sessionId, context, { refresh: retryingRef.current })
      .then((productIds) => {
        if (cancelled) return;
        setProductState({ status: "ready", productIds });
        endRetry();
      })
      .catch((error) => {
        if (cancelled) return;
        DebugLogger.error("Failed to resolve product bindings, offering a retry", {
          sessionId,
          error: String(error),
          code: errorCodeOf(error),
        });
        // A revalidation that fails keeps the last confirmed answer rather than replacing a
        // working menu with an error.
        setProductState((current) =>
          current.status === "ready" ? current : { status: "unknown" },
        );
        endRetry();
      });
    return () => {
      cancelled = true;
    };
  }, [retryToken]);

  const isLoading = productState.status === "loading";
  const visibleItems =
    productState.status === "ready"
      ? menuItems.filter(
          (item) =>
            !item.requiredProduct ||
            productState.productIds.includes(item.requiredProduct),
        )
      : productState.status === "unknown"
        ? menuItems.filter((item) => !item.requiredProduct)
        : [];

  return (
    <>
      <div className="p-6 border-b border-sidebar-border flex-shrink-0">
        <Logo />
      </div>

      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto overscroll-contain">
        {isLoading
          ? Array.from({ length: menuItems.length }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                aria-hidden="true"
                className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-transparent"
              >
                <div className="skeleton-shimmer w-5 h-5 rounded bg-sidebar-accent" />
                <div className="skeleton-shimmer h-3 rounded bg-sidebar-accent flex-1" />
              </div>
            ))
          : visibleItems.map((item, index) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/console"}
                onClick={onNavigate}
                style={{ animationDelay: `${index * STAGGER_MS}ms` }}
                className={({ isActive }) =>
                  `animate-sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg transition-all border-2 ${
                    isActive
                      ? "border-primary text-primary"
                      : "border-transparent text-sidebar-foreground hover:bg-sidebar-accent"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{t(`sidebar.${item.key}`)}</span>
              </NavLink>
            ))}

        {productState.status === "unknown" && (
          <div className="px-4 pt-3 space-y-2" data-testid="sidebar-menu-error">
            <p className="text-xs text-destructive" role="alert">
              {t("sidebar.menuError")}
            </p>
            <button
              type="button"
              data-testid="sidebar-menu-retry"
              aria-busy={retrying}
              onClick={() => {
                if (retryingRef.current) return;
                retryingRef.current = true;
                setRetrying(true);
                setRetryToken((token) => token + 1);
              }}
              className={`text-xs font-medium text-sidebar-foreground underline underline-offset-2 hover:text-primary ${
                retrying ? "opacity-60" : ""
              }`}
            >
              {t("sidebar.retry")}
            </button>
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-sidebar-border flex-shrink-0">
        <ScopeSwitcher
          sessionId={sessionId}
          scopeContext={scopeContext}
          onNavigateAway={onExitNavigate}
        />
      </div>
    </>
  );
};

export const Sidebar = () => (
  <aside className="hidden md:flex w-64 bg-sidebar border-r border-sidebar-border h-screen sticky top-0 flex-col overscroll-none">
    <SidebarContent />
  </aside>
);
